import { FilterValues } from 'src/model';
import { EmployeesRangeValues, MinMaxOptionsValues } from 'src/utils/filterOptions';
import { FiltersArray } from 'src/utils/apollofilterObjContacts';
import { SeniorityDesignation } from 'src/features/Dashboard/utils/ICPSubDesignationValues';
import { excludedICPContactFilters } from 'src/constants/constants';
import { regionalData, WITHOUT_STATE_US_METRO_REGIONS, WITHOUT_STATE_CA_METRO_REGIONS } from 'src/features/multiDbExternal/utils/constants';
import { cataloguedDepartmentsV2, parentDepartmentsV2 } from 'src/components/EmployeeByDept/departments';

interface Filter {
  filterName: string;
  [key: string]: any;
}
export const customICPStringFilterCheck = (filter: any) => {
  return (
    filter.filterKey.startsWith('string') ||
    filter.filterKey.startsWith('singleList') ||
    filter.filterKey.startsWith('multiList')
  );
};

const parseEmployeeDistributionIncludes = (includes: any) => {
  if (!Array.isArray(includes)) {
    return [];
  }
  return includes.map((value: any) => {
    if (typeof value !== 'string') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  });
};

const parseEmployeeByDepartmentData = (includes: any) => {
  if (!Array.isArray(includes) || includes.length === 0) {
    return {};
  }
  const raw = includes[0];
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch (error) {
      return {};
    }
  }
  return typeof raw === 'object' && raw ? raw : {};
};

const parentChildDepartmentSet = new Set<string>(
  parentDepartmentsV2.flatMap((parent) => cataloguedDepartmentsV2?.[parent] || []),
);

const toUiEmployeeByDepartmentData = (source: Record<string, any>) => {
  const normalized = { ...(source || {}) };
  Object.keys(source || {}).forEach((department) => {
    if (department.endsWith('__child')) return;
    if (parentChildDepartmentSet.has(department)) {
      normalized[`${department}__child`] = source[department];
      delete normalized[department];
    }
  });
  return normalized;
};

export const serializeEmployeeByDepartmentForPersistence = (source: Record<string, any>) => {
  const serialized: Record<string, any> = {};
  const input = source || {};

  Object.entries(input).forEach(([department, range]) => {
    if (department.endsWith('__child')) {
      serialized[department.replace('__child', '')] = range;
      return;
    }

    if (parentDepartmentsV2.includes(department)) {
      const subdepartments = cataloguedDepartmentsV2?.[department] || [];
      // Parent selection should persist as full child map for deterministic readback.
      subdepartments.forEach((subdepartment: string) => {
        serialized[subdepartment] = range;
      });
      return;
    }

    serialized[department] = range;
  });

  return serialized;
};

export const normalizeEmployeeByDepartmentForUi = (
  source: Record<string, any>,
  includeList: any[] = [],
) => {
  const normalized = { ...(source || {}) };
  const includeDepartments = new Set(
    (includeList || [])
      .map((entry: any) => entry?.department)
      .filter((dept: any): dept is string => typeof dept === 'string' && dept.length > 0),
  );
  const sourceKeys = Object.keys(source || {});

  // If all children of a parent are present with identical range, collapse to parent for UI hydration.
  parentDepartmentsV2.forEach((parent) => {
    const childDepartments = cataloguedDepartmentsV2?.[parent] || [];
    if (!Array.isArray(childDepartments) || childDepartments.length === 0) return;

    const allChildrenPresent = childDepartments.every((child: string) => normalized[child]);
    if (!allChildrenPresent) return;

    const firstChildRange = normalized[childDepartments[0]];
    const sameRangeForAllChildren = childDepartments.every((child: string) => {
      const current = normalized[child];
      return JSON.stringify(current || {}) === JSON.stringify(firstChildRange || {});
    });
    if (!sameRangeForAllChildren) return;

    childDepartments.forEach((child: string) => {
      delete normalized[child];
    });
    normalized[parent] = firstChildRange;
  });

  sourceKeys.forEach((department) => {
    if (department.endsWith('__child')) return;

    const isParent = parentDepartmentsV2.includes(department);
    const isAmbiguous = parentChildDepartmentSet.has(department);
    if (!isAmbiguous) return;

    if (!isParent) {
      normalized[`${department}__child`] = source[department];
      delete normalized[department];
      return;
    }

    const childDepartments = cataloguedDepartmentsV2?.[department] || [];
    const hasSiblingSelectedInSource = childDepartments.some(
      (child: string) => child !== department && sourceKeys.includes(child),
    );
    const hasSiblingInIncludeList = childDepartments.some(
      (child: string) => child !== department && includeDepartments.has(child),
    );

    if (!hasSiblingSelectedInSource && !hasSiblingInIncludeList) {
      normalized[`${department}__child`] = source[department];
      delete normalized[department];
    }
  });

  return normalized;
};

const isEmployeeCountFilter = (filterKey: any) =>
  typeof filterKey === 'string' && filterKey.trim().toLowerCase() === 'employeecount';

const parseEmployeeCountRange = (value: any) => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) return null;
  return { min: Number(match[1]), max: Number(match[2]) };
};

const resolveEmployeeCountRange = (filter: any) => {
  if (Array.isArray(filter?.range) && filter.range.length > 0) {
    return filter.range;
  }
  if (!Array.isArray(filter?.includes)) return [];
  return filter.includes.map(parseEmployeeCountRange).filter(Boolean);
};

export const transformAPIResponseToStateFormat = (input: any) => {
  const stateFilterObj: any = {};
  const isLocationFilterKey = (key: string) => {
    const normalized = (key || '').toLowerCase().replace(/\s+/g, '');
    return (
      normalized === 'location' ||
      normalized === 'accounthqlocation' ||
      normalized === 'contactlocation'
    );
  };
  for (let i = 0; i < input.length; i++) {
    const rawFilterKey = (input[i]?.filterKey || '').trim();
    const isGroupedEmployeeCount =
      input[i]?.filterGroup && isEmployeeCountFilter(rawFilterKey);

    if (isGroupedEmployeeCount) {
      stateFilterObj.employeeCount = {
        filterName: 'Employee Count',
        filterKey: 'employeeCount',
        filterData: {
          min: input[i].min,
          max: input[i].max,
          range: resolveEmployeeCountRange(input[i]),
        },
      };
      continue;
    }

    if (input[i]?.filterGroup && input[i]?.filterGroup !== 'Buying Intent') {
      const childFilterKey = rawFilterKey;
      stateFilterObj[`${input[i]?.filterGroupKey}`] = {
        ...stateFilterObj[`${input[i]?.filterGroupKey}`],
        filterName: input[i]?.filterGroup,
        filterKey: input[i]?.filterGroupKey,
        filterData: {
          ...stateFilterObj[`${input[i]?.filterGroupKey}`]?.filterData,
          [childFilterKey]: {},
        },
      };
      if (input[i].filterType === 'min-max') {
        stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[`${childFilterKey}`] = {
          ...(input[i].min && { min: input[i].min }),
          ...(input[i].max && { max: input[i].max }),
          range: input[i].range,
        };
      } else if (input[i].filterType === 'single-select') {
        if (input[i]?.include?.length > 0) {
          stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[`${input[i].filterKey}`] = {
            includes: input[i]?.include[0] || '',
          };
        }
        if (input[i]?.exclude?.length > 0) {
          stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[`${input[i].filterKey}`] = {
            excludes: input[i]?.exclude[0] || '',
          };
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[`${input[i].filterKey}`] = {
        //     range: input[i]?.range[0] || '',
        //   };
        // }
      } else if (input[i].filterType === 'date-range') {
        if (input[i].toDate && input[i].fromDate) {
          if (
            !(
              input[i]?.toDate === 'NaN-NaN-NaN NaN:NaN:NaN' ||
              input[i]?.fromDate === 'NaN-NaN-NaN NaN:NaN:NaN'
            )
          ) {
            stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[`${childFilterKey}`] = {
              fromDate: input[i].fromDate,
              toDate: input[i].toDate,
            };
          }
        }
      } else {
        if (input[i]?.includes?.length > 0) {
          const parsedIncludes =
            input[i]?.filterKey === 'employeeDistribution'
              ? parseEmployeeDistributionIncludes(input[i]?.includes)
              : input[i]?.includes;
          stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[
            `${childFilterKey}`
          ].includes = parsedIncludes;
        }
        if (input[i]?.excludes?.length > 0) {
          stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[
            `${childFilterKey}`
          ].excludes = input[i]?.excludes;
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterGroupKey}`].filterData[
        //     `${input[i].filterKey}`
        //   ].range = input[i]?.range;
        // }
      }
    } else {
      const filterKeyForState = isLocationFilterKey(rawFilterKey) ? 'location' : rawFilterKey;
      stateFilterObj[`${filterKeyForState}`] = {
        filterName: input[i]?.filterName,
        filterKey: filterKeyForState,
        filterData: {},
      };
      if (input[i].filterType === 'min-max') {
        stateFilterObj[`${filterKeyForState}`].filterData = {
          min: input[i].min,
          max: input[i].max,
          range: input[i].range,
        };
      } else if (
        input[i].filterType === 'emp-by-dept' ||
        input[i].filterType === 'employee-by-dept' ||
        input[i].filterKey === 'employeeByDepartment'
      ) {
        const savedData = parseEmployeeByDepartmentData(input[i].includes);
        stateFilterObj[`${filterKeyForState}`].filterData = normalizeEmployeeByDepartmentForUi(
          savedData,
          input[i]?.includeList || [],
        );
      } else if (input[i].filterType === 'single-select') {
        if (input[i]?.includes?.length > 0) {
          stateFilterObj[`${filterKeyForState}`].filterData.includes = input[i]?.includes[0];
        }
        if (input[i]?.excludes?.length > 0) {
          stateFilterObj[`${filterKeyForState}`].filterData.excludes = input[i]?.excludes[0];
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterKey}`].filterData.range = input[i]?.range[0];
        // }
      } else {
        if (input[i]?.includes?.length > 0) {
          const parsedIncludes =
            input[i]?.filterKey?.trim() === 'employeeDistribution'
              ? parseEmployeeDistributionIncludes(input[i]?.includes)
              : input[i]?.includes;
          if (isLocationFilterKey(filterKeyForState)) {
            stateFilterObj[`${filterKeyForState}`].filterData['location'] = {
              ...(stateFilterObj[`${filterKeyForState}`].filterData['location'] || {}),
              includes: parsedIncludes,
            };
          } else {
            stateFilterObj[`${filterKeyForState}`].filterData['includes'] = parsedIncludes;
          }
        }
        if (input[i]?.excludes?.length > 0) {
          if (isLocationFilterKey(filterKeyForState)) {
            stateFilterObj[`${filterKeyForState}`].filterData['location'] = {
              ...(stateFilterObj[`${filterKeyForState}`].filterData['location'] || {}),
              excludes: input[i]?.excludes,
            };
          } else {
            stateFilterObj[`${filterKeyForState}`].filterData['excludes'] = input[i]?.excludes;
          }
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterKey}`].filterData['range'] = input[i]?.range;
        // }
      }
    }
  }
  return stateFilterObj;
};

export const normalizeIcpRendererFilterKey = (key: any) => {
  if (typeof key !== 'string') return key;
  const trimmedKey = key.trim();
  const normalizedKey = trimmedKey.toLowerCase().replace(/\s+/g, '');
  if (normalizedKey === 'employeecount') {
    return 'employeeCount';
  }
  return trimmedKey;
};

export const transformAPIResponseToStateFormatWithFilterName = (input: any) => {
  const stateFilterObj: any = {};
  const isLocationFilterKey = (key: string) => {
    const normalized = (key || '').toLowerCase().replace(/\s+/g, '');
    return (
      normalized === 'location' ||
      normalized === 'accounthqlocation' ||
      normalized === 'contactlocation'
    );
  };
  for (let i = 0; i < input.length; i++) {
    const rawFilterKey = normalizeIcpRendererFilterKey(input[i]?.filterKey || input[i]?.filterName);
    const isGroupedEmployeeCount =
      input[i]?.filterGroup && isEmployeeCountFilter(rawFilterKey);

    if (isGroupedEmployeeCount) {
      stateFilterObj.employeeCount = {
        filterName: 'Employee Count',
        filterKey: 'employeeCount',
        filterData: {
          min: input[i].min,
          max: input[i].max,
          range: resolveEmployeeCountRange(input[i]),
        },
      };
      continue;
    }

    if (input[i]?.filterGroup) {
      const groupKey = input[i]?.filterGroupKey || input[i]?.filterGroup;
      const childFilterKey = rawFilterKey;
      stateFilterObj[`${groupKey}`] = {
        ...stateFilterObj[`${groupKey}`],
        filterName: input[i]?.filterGroup,
        filterKey: groupKey,
        filterData: {
          ...stateFilterObj[`${groupKey}`]?.filterData,
          [childFilterKey]: {},
        },
      };
      if (input[i].filterType === 'min-max') {
        stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`] = {
          min: input[i].min,
          max: input[i].max,
        };
      } else if (input[i].filterType === 'single-select') {
        if (input[i]?.include?.length > 0) {
          stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`] = {
            includes: input[i]?.include[0] || '',
          };
        }
        if (input[i]?.exclude?.length > 0) {
          stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`] = {
            excludes: input[i]?.exclude[0] || '',
          };
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterGroup}`].filterData[`${input[i].filterName}`] = {
        //     range: input[i]?.range[0] || '',
        //   };
        // }
      } else if (input[i].filterType === 'date-range') {
        if (input[i].toDate && input[i].fromDate) {
          stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`] = [
            input[i].fromDate,
            input[i].toDate,
          ];
        }
      } else {
        if (input[i]?.includes?.length > 0) {
          const parsedIncludes =
            input[i]?.filterName === 'employeeDistribution' || input[i]?.filterName === 'Global Presence'
              ? parseEmployeeDistributionIncludes(input[i]?.includes)
              : input[i]?.includes;
          stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`].includes =
            parsedIncludes;
        }
        if (input[i]?.excludes?.length > 0) {
          stateFilterObj[`${groupKey}`].filterData[`${childFilterKey}`].excludes =
            input[i]?.excludes;
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterGroup}`].filterData[`${input[i].filterName}`].range =
        //     input[i]?.range;
        // }
      }
    } else {
      const rawFilterKey = normalizeIcpRendererFilterKey(input[i]?.filterKey || input[i]?.filterName);
      const filterKeyForState = isLocationFilterKey(rawFilterKey) ? 'location' : rawFilterKey;
      stateFilterObj[`${filterKeyForState}`] = {
        filterName: input[i]?.filterName,
        filterKey: filterKeyForState,
        filterData: {},
      };
      if (input[i].filterType === 'min-max') {
        stateFilterObj[`${filterKeyForState}`].filterData = {
          min: input[i].min,
          max: input[i].max,
        };
      } else if (
        input[i].filterType === 'emp-by-dept' ||
        input[i].filterType === 'employee-by-dept' ||
        input[i].filterName === 'employeeByDepartment' ||
        input[i].filterName === 'Employee By Department' ||
        input[i].filterName === 'Employee By Dept.'
      ) {
        const savedData = parseEmployeeByDepartmentData(input[i].includes);
        stateFilterObj[`${filterKeyForState}`].filterData = normalizeEmployeeByDepartmentForUi(
          savedData,
          input[i]?.includeList || [],
        );
      } else if (input[i].filterType === 'single-select') {
        if (input[i]?.includes?.length > 0) {
          stateFilterObj[`${filterKeyForState}`].filterData.includes = input[i]?.includes[0];
        }
        if (input[i]?.excludes?.length > 0) {
          stateFilterObj[`${filterKeyForState}`].filterData.excludes = input[i]?.excludes[0];
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterName}`].filterData.range = input[i]?.range[0];
        // }
      } else {
        if (input[i]?.includes?.length > 0) {
          const parsedIncludes =
            input[i]?.filterName === 'employeeDistribution' || input[i]?.filterName === 'Global Presence'
              ? parseEmployeeDistributionIncludes(input[i]?.includes)
              : input[i]?.includes;
          if (isLocationFilterKey(filterKeyForState)) {
            stateFilterObj[`${filterKeyForState}`].filterData['location'] = {
              ...(stateFilterObj[`${filterKeyForState}`].filterData['location'] || {}),
              includes: parsedIncludes,
            };
          } else {
            stateFilterObj[`${filterKeyForState}`].filterData['includes'] = parsedIncludes;
          }
        }
        if (input[i]?.excludes?.length > 0) {
          if (isLocationFilterKey(filterKeyForState)) {
            stateFilterObj[`${filterKeyForState}`].filterData['location'] = {
              ...(stateFilterObj[`${filterKeyForState}`].filterData['location'] || {}),
              excludes: input[i]?.excludes,
            };
          } else {
            stateFilterObj[`${filterKeyForState}`].filterData['excludes'] = input[i]?.excludes;
          }
        }
        // if (input[i]?.range?.length > 0) {
        //   stateFilterObj[`${input[i]?.filterName}`].filterData['range'] = input[i]?.range;
        // }
      }
    }
  }
  return stateFilterObj;
};

export function transformFilters(input: any[]): any[] {
  const filterGroups: { [key: string]: any } = {};

  for (const filter of input) {
    const groupKey = normalizeIcpRendererFilterKey(filter.filterGroupKey);
    const childKey = normalizeIcpRendererFilterKey(filter?.filterKey || '');
    if (!filterGroups[groupKey]) {
      filterGroups[groupKey] = {
        label: filter.filterGroup,
        key: groupKey,
        type: '',
        fetchUrl: filter.fetchUrl,

        valueKey: filter?.valueKey || 'name',
        labelKey: filter?.labelKey || 'name',
        scope: [],
        fetch: false,
        filterData: {
          childFilters: [],
        },
      };
    }

    const childFilter: any = {
      label: filter?.FilterName || '',
      key: childKey,
      type: filter?.filterType || '',
      fetchUrl: filter?.fetchUrl || '',
      scope: filter?.scope || (filter?.filterType === 'min-max' ? [] : []),
      fetch: false,
      valueKey: filter?.valueKey || 'name',
      labelKey: filter?.labelKey || 'name',
      filterData: {
        filterValues: {},
      },
    };

    childFilter.filterData.filterValues = generateFilterValues(childFilter, filter.UniqueValues);

    filterGroups[groupKey].filterData.childFilters.push(childFilter);
  }

  return Object.values(filterGroups);
}

const normalizeIcpChildFilter = (childFilter: any) => {
  const normalizedIsAccountFilter =
    childFilter?.isAccountFilters ?? childFilter?.accountFilters ?? false;
  const normalizedAccountFilter =
    childFilter?.accountFilters ?? childFilter?.isAccountFilters ?? false;
  const normalizedChild: any = {
    label: childFilter?.label || childFilter?.FilterName || '',
    key: normalizeIcpRendererFilterKey(childFilter?.key || childFilter?.filterKey || ''),
    type: childFilter?.type || childFilter?.filterType || '',
    fetchUrl: childFilter?.fetchUrl || childFilter?.fetchURL || '',
    scope: childFilter?.scope || (childFilter?.filterType === 'min-max' ? [] : []),
    fetch: Boolean(childFilter?.fetch || childFilter?.fetchUrl || childFilter?.fetchURL),
    valueKey: childFilter?.valueKey || 'name',
    labelKey: childFilter?.labelKey || 'name',
    isAccountFilters: normalizedIsAccountFilter,
    accountFilters: normalizedAccountFilter,
    ...(childFilter?.addCategory !== undefined && { addCategory: childFilter.addCategory }),
    filterData: {
      ...(childFilter?.filterData || {}),
      filterValues: childFilter?.filterData?.filterValues || {},
    },
  };

  if (
    !normalizedChild.filterData.filterValues ||
    (Array.isArray(normalizedChild.filterData.filterValues) &&
      normalizedChild.filterData.filterValues.length === 0)
  ) {
    normalizedChild.filterData.filterValues = generateFilterValues(
      normalizedChild,
      childFilter?.UniqueValues || '',
    );
  }

  return normalizedChild;
};

export const buildIcpNestedFilter = (filter: any) => {
  const parentIsAccountFilter = filter?.isAccountFilters ?? filter?.accountFilters ?? false;
  const parentAccountFilter = filter?.accountFilters ?? filter?.isAccountFilters ?? false;
  const childFilters = (filter?.filterData?.childFilters || []).map((child: any) =>
    normalizeIcpChildFilter({
      ...child,
      isAccountFilters: child?.isAccountFilters ?? child?.accountFilters ?? parentIsAccountFilter,
      accountFilters: child?.accountFilters ?? child?.isAccountFilters ?? parentAccountFilter,
    }),
  );

  return {
    label: filter?.label || filter?.FilterName || filter?.filterGroup || '',
    key: normalizeIcpRendererFilterKey(filter?.key || filter?.filterKey || filter?.filterGroupKey || ''),
    type: filter?.type || filter?.filterType || '',
    scope: filter?.scope || [],
    fetch: Boolean(filter?.fetch || filter?.fetchUrl || filter?.fetchURL),
    valueKey: filter?.valueKey || 'name',
    labelKey: filter?.labelKey || 'name',
    fetchUrl: filter?.fetchUrl || filter?.fetchURL || '',
    isAccountFilters: parentIsAccountFilter,
    accountFilters: parentAccountFilter,
    filterData: {
      childFilters,
    },
  };
};

export const flattenIcpApiFilters = (filters: any[] = []) => {
  const flattened: any[] = [];
  const seen = new Set<string>();

  const addIfUnique = (filter: any) => {
    const normalizedFilter = {
      ...filter,
      filterKey: normalizeIcpRendererFilterKey(filter?.filterKey || ''),
    };
    const key = `${normalizedFilter?.filterKey || ''}::${normalizedFilter?.FilterName || ''}::${normalizedFilter?.primaryKey || ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      flattened.push(normalizedFilter);
    }
  };

  filters.forEach((filter) => {
    addIfUnique(filter);

    const childFilters = filter?.filterData?.childFilters || [];
    if (Array.isArray(childFilters) && childFilters.length > 0) {
      childFilters.forEach((child: any) => {
        addIfUnique({
          ...child,
          FilterName: child?.FilterName || child?.label || '',
          filterKey: child?.filterKey || child?.key || '',
          filterType: child?.filterType || child?.type || '',
          fetchUrl: child?.fetchUrl || child?.fetchURL || '',
          scope: child?.scope || [],
          valueKey: child?.valueKey || 'name',
          labelKey: child?.labelKey || 'name',
          subCategory: child?.subCategory || null,
          filterGroup: filter?.filterGroup || null,
          filterGroupKey: filter?.filterGroupKey || null,
          isAccountFilters:
            child?.isAccountFilters ??
            child?.accountFilters ??
            filter?.isAccountFilters ??
            filter?.accountFilters,
          accountFilters:
            child?.accountFilters ??
            child?.isAccountFilters ??
            filter?.accountFilters ??
            filter?.isAccountFilters,
          primaryKey: child?.primaryKey || child?.icpViewFilterPrimaryKey || filter?.primaryKey,
          UniqueValues: child?.UniqueValues || null,
        });
      });
    }
  });

  return flattened;
};

export const generateFilterValues = (filterObj: FiltersArray, uniqueValues: string) => {
  switch (filterObj.type) {
    case 'multi-select':
      return generateSelectDropdownValues(uniqueValues);

    case 'single-select':
      return generateSelectDropdownValues(uniqueValues);

    case 'min-max':
      if (filterObj.key.includes('employee')) {
        return generateMinMaxValues('employee');
      } else if (
        filterObj.key.toLowerCase().includes('revenue') ||
        filterObj.key.toLowerCase().includes('amount')
      ) {
        return generateMinMaxValues('revenue');
      } else if (filterObj.key.toLowerCase().includes('year')) {
        return generateMinMaxValues('year');
      } else {
        return generateMinMaxValues('default');
      }

    case 'ICP-designation':
      return SeniorityDesignation;

    case 'radio-button-group':
    case 'checkbox-button-group':
      return generateRadioGroupOptions(uniqueValues);

    case 'nested-checkbox':
      return parseUniqueValuesAsObject(uniqueValues);

    case 'preload-nested-checkbox':
      return [];

    case 'async-multi-select':
      // Support static async-multi-select filters (no fetchUrl) that provide UniqueValues.
      return uniqueValues ? generateSelectDropdownValues(uniqueValues) : [];

    case 'emp-by-dept':
      return generateSelectDropdownValues(uniqueValues);

    case 'free-text':
    case 'free-text-with-clause':
    case 'async-tag-picker':
    case 'async-multi-select-with-clause':
      return [];

    default:
      return [];
  }
};

export const generateRadioGroupOptions = (uniqueValues: string) => {
  const radioOptions: FilterValues = [];
  const options = parseUniqueValuesAsArray(uniqueValues);
  for (let i = 0; i < options.length; i++) {
    radioOptions.push({
      label: options[i]?.label ? options[i]?.label : options[i],
      id: options[i]?.id ? options[i]?.id : options[i],
    });
  }
  return radioOptions;
};

export const generateSelectDropdownValues = (uniqueValues: string) => {
  const multiSelectValues: FilterValues = [];
  const values = parseUniqueValuesAsArray(uniqueValues);
  for (let i = 0; i < values.length; i++) {
    multiSelectValues.push({
      label: values[i],
      id: values[i],
    });
  }
  return multiSelectValues;
};

const parseUniqueValues = (uniqueValues: any) => {
  if (uniqueValues === null || uniqueValues === undefined) return null;
  if (Array.isArray(uniqueValues) || typeof uniqueValues === 'object') return uniqueValues;
  if (typeof uniqueValues !== 'string') return null;
  const trimmed = uniqueValues.trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return null;
  }
};

const parseUniqueValuesAsArray = (uniqueValues: any): any[] => {
  const parsed = parseUniqueValues(uniqueValues);
  return Array.isArray(parsed) ? parsed : [];
};

const parseUniqueValuesAsObject = (uniqueValues: any): Record<string, any> => {
  const parsed = parseUniqueValues(uniqueValues);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
};

export const generateMinMaxValues = (type: string) => {
  switch (type) {
    case 'revenue':
      return MinMaxOptionsValues;

    case 'employee':
      return EmployeesRangeValues;

    case 'year':
      return generateYearValues();

    default:
      return EmployeesRangeValues;
  }
};

export function addPrimaryKeyInExtractedFilters(array1: any[], array2: any[]): any[] {
  const result = array1.map((filter1Item) => {
    const matchingFilter2 = array2.find(
      (filter2Item) => filter1Item.filterName === filter2Item.filterKey,
    );

    if (matchingFilter2) {
      // Use filter config as the source of truth for metadata sent to the API.
      filter1Item.icpViewFilterPrimaryKey = matchingFilter2.primaryKey;
      filter1Item.filterName = matchingFilter2.FilterName || filter1Item.filterName;
    }

    return filter1Item;
  });
  return result;
}

// interface IUserCustomerFilters {
//   icpViewFilterPrimaryKey: number;
//   min?: string;
//   max?: string;
//   toDate?: string;
//   fromDate?: string;
//   includes?: string[];
//   excludes?: string[];
// }

// type UserCustomerFiltersType = IUserCustomerFilters[];

// const deleteExtraProperties = (obj: IUserCustomerFilters) => {
//   console.log(obj)
//   if (obj.min === '') {
//     delete obj.min;
//   }
//   if (obj.max === '') {
//     delete obj.max;
//   }
//   if (obj.toDate === '') {
//     delete obj.toDate;
//   }
//   if (obj.fromDate === '') {
//     delete obj.fromDate;
//   }
// };

// function removeEmptyObjects(data: UserCustomerFiltersType) {
//   console.log({data})
//   const filteredData = data.filter((obj) => {
//     const isEmptyStringOrArray = (value: any) => {
//       if (Array.isArray(value)) {
//         return value.length === 0;
//       }
//       return value === '';
//     };
//     deleteExtraProperties(obj);
//     return !(
//       isEmptyStringOrArray(obj.min) ||
//       isEmptyStringOrArray(obj.max) ||
//       isEmptyStringOrArray(obj.toDate) ||
//       isEmptyStringOrArray(obj.fromDate) ||
//       isEmptyStringOrArray(obj.includes) ||
//       isEmptyStringOrArray(obj.excludes)
//     );
//   });
//   return filteredData;
// }

/**
 * Before save (create/update/clone): stringify each entry in filterData.includes for
 * employeeDistribution so the API receives strings; GET responses are parsed server-side.
 */
export const applyStringifiedIncludesForEmployeeDistribution = (
  newAppliedFilters: Record<string, any>,
) => {
  const key = 'employeeDistribution';
  const filter = newAppliedFilters[key];
  const includes = filter?.filterData?.includes;
  if (!filter?.filterData || includes == null) {
    return;
  }
  const toStrings = (raw: unknown): string[] => {
    if (Array.isArray(raw)) {
      return raw.map((item) => (typeof item === 'string' ? item : JSON.stringify(item)));
    }
    if (typeof raw === 'object') {
      return [JSON.stringify(raw)];
    }
    return [String(raw)];
  };
  newAppliedFilters[key] = {
    ...filter,
    filterData: {
      ...filter.filterData,
      includes: toStrings(includes),
    },
  };
};

export function extractFiltersFunction(input: Record<string, any>): Filter[] {
  const filters: Filter[] = [];

  function extractFilters(filterData: Record<string, any>, filterName: string) {
    const filter: Filter = {
      filterName,
    };

    let hasProperties = false; // Flag to check if non-filterName properties exist

    for (const key in filterData) {
      if (key) {
        if (typeof filterData[key] === 'object' && !Array.isArray(filterData[key])) {
          const subFilters = extractFilters(filterData[key], key);
          filter[key] = subFilters;
        } else {
          if (key !== 'filterName') {
            filter[key] = filterData[key];
            hasProperties = true; // Mark that non-filterName properties exist
          }
        }
      }
    }

    if (hasProperties) {
      filters.push(filter); // Only push if non-filterName properties exist
    }
  }

  for (const key in input) {
    if (key) {
      extractFilters(input[key]['filterData'], key);
    }
  }

  return filters;
}

export const generateYearValues = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  const yearsToDisplay = 1000;

  for (let i = currentYear; i >= currentYear - yearsToDisplay; i--) {
    years.push({ label: i.toString(), id: i });
  }
  return years;
};

export function convertDateToFormattedString(dateString: string): string | null {
  try {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error(`Error converting date: ${error}`);
    return null; // Return null in case of an error
  }
}

/**
 * Converts date strings with timezone info to formatted start and end date range
 * @param {string} startDateString - Date string like "Sun Jul 27 2025 00:00:00 GMT+0530 (India Standard Time)"
 * @param {string} endDateString - Date string like "Tue Jul 29 2025 00:00:00 GMT+0530 (India Standard Time)"
 * @return {Object|null} Object with fromDate and toDate in format "YYYY-MM-DD HH:mm:ss"
 * @example
 * convertDateStringToRange(
 *   "Sun Jul 27 2025 00:00:00 GMT+0530 (India Standard Time)",
 *   "Tue Jul 29 2025 00:00:00 GMT+0530 (India Standard Time)"
 * )
 * // Returns: { fromDate: "2025-07-27 00:00:00", toDate: "2025-07-29 23:59:59" }
 */
export function convertDateStringToRange(
  startDateString: string,
  endDateString: string,
): { fromDate: string; toDate: string } | null {
  try {
    // Parse the start date
    const startDate = new Date(startDateString);
    if (isNaN(startDate.getTime())) {
      throw new Error(`Invalid start date: ${startDateString}`);
    }

    // Parse the end date
    const endDate = new Date(endDateString);
    if (isNaN(endDate.getTime())) {
      throw new Error(`Invalid end date: ${endDateString}`);
    }

    // Format start date as YYYY-MM-DD 00:00:00
    const startYear = startDate.getFullYear();
    const startMonth = (startDate.getMonth() + 1).toString().padStart(2, '0');
    const startDay = startDate.getDate().toString().padStart(2, '0');
    const fromDate = `${startYear}-${startMonth}-${startDay} 00:00:00`;

    // Format end date as YYYY-MM-DD 23:59:59
    const endYear = endDate.getFullYear();
    const endMonth = (endDate.getMonth() + 1).toString().padStart(2, '0');
    const endDay = endDate.getDate().toString().padStart(2, '0');
    const toDate = `${endYear}-${endMonth}-${endDay} 23:59:59`;

    return {
      fromDate,
      toDate,
    };
  } catch (error) {
    console.error(`Error converting date range: ${error}`);
    return null;
  }
}

export const areContactFiltersApplied = (appliedFilters: any) => {
  const activeFilters = Object?.values(appliedFilters)?.filter((filter: any) => {
    const { filterName, filterData } = filter;
    if (!excludedICPContactFilters?.includes(filterName)) {
      if (filterData?.includes && filterData?.includes?.length > 0) return true;
      if (filterData?.excludes && filterData?.excludes?.length > 0) return true;
      if (filterData?.range && filterData?.range?.length > 0) return true;
      if (filterData?.min !== undefined || filterData?.max !== undefined) return true;
      if (
        filterData?.totalFundingAmount &&
        (filterData?.totalFundingAmount.min !== undefined ||
          filterData?.totalFundingAmount.max !== undefined)
      )
        return true;
    }
    return false;
  });

  return activeFilters.length > 0;
};

export const getFilterWeightageKey = (filterName: string) => {
  const filterObj: Record<string, string> = {
    employeeCount: 'empCountWeightage',
    revenue: 'revenueWeightage',
    totalFundingAmount: 'totalFunAmtWeightage',
    latestFundingAmount: 'latestFunAmtWeightage',
    latestFundingDate: 'latestFunDateWeightage',
    employeeByDepartment: 'empByDeptWeightage',
    foundedYear: 'foundedYearWeightage',
  };
  return filterObj[filterName.trim()];
};

export const canonicalizeIcpSearchCountFilterKey = (rawKey: string, isAccountScoped = false) => {
  const normalizedKey = (rawKey || '').toLowerCase().replace(/\s+/g, '');
  switch (normalizedKey) {
    case 'b2b_b2c':
      return 'businessClassification';
    case 'profitnonprofit':
      return 'profitNonProfit';
    case 'govtnongovt':
      return 'govtNonGovt';
    case 'employeecount':
      return 'totalEmployeeCount';
    case 'latestfundingamount':
      return 'lastFundingAmount';
    case 'totalfundingamount':
      return 'totalFunding';
    case 'latestfundingdate':
      return 'lastFundingDate';
    case 'latestfundingstage':
      return 'stages';
    case 'department':
      return 'catalogerDivisions';
    case 'designation':
      return 'catalogerDesignations';
    case 'industry':
      return isAccountScoped ? 'subIndustry' : 'industry';
    default:
      return (rawKey || '').trim();
  }
};

export const canonicalizeIcpSearchCountFilterName = (rawKey: string, fallback = '') => {
  const normalizedKey = (rawKey || '').toLowerCase().replace(/\s+/g, '');
  const labels: Record<string, string> = {
    b2b_b2c: 'B2B / B2C',
    profitnonprofit: 'Profit / Non Profit',
    govtnongovt: 'Govt / Non Govt',
    employeecount: 'Employee Count',
    latestfundingamount: 'Latest Funding Amount',
    totalfundingamount: 'Total Funding Amount',
    latestfundingdate: 'Latest Funding Date',
    latestfundingstage: 'Latest Funding Stage',
    employeebydepartment: 'Employee By Dept.',
    department: 'Department',
    designation: 'Seniority',
  };
  return labels[normalizedKey] || fallback || rawKey;
};

const _locationCategoryMap: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const allItems = [
    ...Object.values(regionalData).flat(),
    ...WITHOUT_STATE_US_METRO_REGIONS,
    ...WITHOUT_STATE_CA_METRO_REGIONS,
  ];
  allItems.forEach((item: string) => {
    if (typeof item === 'string' && item.includes('::')) {
      const sepIdx = item.lastIndexOf('::');
      const name = item.substring(0, sepIdx).toLowerCase();
      const category = item.substring(sepIdx + 2);
      if (!map[name]) map[name] = category;
    }
  });
  return map;
})();

export const resolveLocationCategory = (bareName: string): string => {
  if (!bareName) return 'country';
  return _locationCategoryMap[bareName.toLowerCase()] || 'country';
};

export const getIcpFilterGroupFallback = (key: string) => {
  const normalized = (key || '').toLowerCase().replace(/\s+/g, '');
  if (
    normalized === 'latestfundingamount' ||
    normalized === 'totalfundingamount' ||
    normalized === 'latestfundingdate' ||
    normalized === 'latestfundingstage' ||
    normalized === 'lastfundingamount' ||
    normalized === 'totalfunding' ||
    normalized === 'lastfundingdate' ||
    normalized === 'stages'
  ) {
    return { key: 'funding', name: 'Funding' };
  }
  return { key: null, name: null };
};

export const regroupIcpSearchCountFilters = (normalizedItems: any[] = []) => {
  const groupedMap = new Map<string, any>();
  const result: any[] = [];

  normalizedItems.forEach((item: any) => {
    const groupKey = item?.__groupKey;
    const groupName = item?.__groupName;

    if (!groupKey) {
      result.push(item);
      return;
    }

    if (!groupedMap.has(groupKey)) {
      const groupedFilter = {
        filter_name: groupName || groupKey,
        filter_key: groupKey,
        filter_data: {},
        __isAccountScoped: item?.__isAccountScoped,
      };
      groupedMap.set(groupKey, groupedFilter);
      result.push(groupedFilter);
    }

    const grouped = groupedMap.get(groupKey);
    grouped.filter_data[item.filter_key] = item.filter_data;
  });

  return result;
};

export const compactNonEmptyObject = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, val]) => {
      if (val === undefined || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'object') return Object.keys(val).length > 0;
      return true;
    }),
  );

const normalizeProfitValues = (values: any[] = []) =>
  values.map((value) => (value === 'For-Profit' ? 'Profit' : value));

export const normalizeIcpSearchCountFilterData = (
  canonicalFilterKey: string,
  filterData: Record<string, any>,
) => {
  if (!filterData || typeof filterData !== 'object') {
    return filterData;
  }

  const sanitizedRange =
    Array.isArray(filterData.range) && filterData.range.length > 0
      ? filterData.range
          .map((item: any) =>
            item && typeof item === 'object'
              ? Object.fromEntries(
                  Object.entries(item).filter(([_, val]) => val !== undefined && val !== null),
                )
              : item,
          )
          .filter((item: any) => {
            if (!item || typeof item !== 'object') return false;
            return Object.keys(item).length > 0;
          })
      : undefined;

  if (canonicalFilterKey === 'profitNonProfit') {
    return {
      ...filterData,
      ...(sanitizedRange ? { range: sanitizedRange } : {}),
      ...(Array.isArray(filterData.includes)
        ? { includes: normalizeProfitValues(filterData.includes) }
        : {}),
      ...(Array.isArray(filterData.excludes)
        ? { excludes: normalizeProfitValues(filterData.excludes) }
        : {}),
    };
  }

  if (canonicalFilterKey === 'foundedYear') {
    const firstRange = Array.isArray(sanitizedRange) && sanitizedRange.length > 0
      ? sanitizedRange[0]
      : null;
    const existingYearRange =
      filterData?.yearRange && typeof filterData.yearRange === 'object'
        ? filterData.yearRange
        : null;
    const normalizedYearRange = existingYearRange || firstRange;
    if (normalizedYearRange && typeof normalizedYearRange === 'object') {
      return { yearRange: normalizedYearRange };
    }
    return {};
  }

  if (canonicalFilterKey === 'lastFundingDate') {
    const between = Array.isArray(filterData?.between) ? filterData.between : [];
    if (between.length === 2) {
      return { between: [String(between[0]).split(' ')[0], String(between[1]).split(' ')[0]] };
    }
    const fromDate = filterData?.from_date || filterData?.fromDate;
    const toDate = filterData?.to_date || filterData?.toDate;
    if (fromDate && toDate) {
      return { between: [String(fromDate).split(' ')[0], String(toDate).split(' ')[0]] };
    }
    return {};
  }

  if (sanitizedRange) {
    return {
      ...filterData,
      range: sanitizedRange,
    };
  }

  return filterData;
};

export const extractedFiltersUpdated = (
  extractedFilters: any[],
  filtersFromStore: any[],
  rawFilters: any[],
) => {
  const parseMaybeJson = (value: any) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const normalizeEmployeeByDeptData = (source: Record<string, any>) => {
    return serializeEmployeeByDepartmentForPersistence(source || {});
  };

  const toDeptIncludeList = (deptData: Record<string, any>, previousIncludeList: any[] = []) => {
    const includeListByDept = new Map<string, any>();
    previousIncludeList.forEach((entry: any) => {
      if (!entry || typeof entry !== 'object') return;
      const dept = entry.department;
      if (dept) {
        includeListByDept.set(dept, entry);
      }
    });

    return Object.entries(deptData || {}).map(([dept, range]: [string, any]) => {
      const prev = includeListByDept.get(dept) || {};
      const weight =
        Object.values(prev).find((val) => typeof val === 'number' && !Number.isNaN(val)) || 2;
      const currentMin = range?.min;
      const currentMax = range?.max;
      const rangeFromArray =
        Array.isArray(range?.range) && range.range.length > 0 ? range.range[0] : null;
      const prevRangeKey = Object.keys(prev || {}).find(
        (key) => key !== 'department' && key !== 'type' && /^\d+\-\d+$/.test(key),
      );
      const [prevMin, prevMax] = prevRangeKey
        ? prevRangeKey.split('-').map((value) => Number(value))
        : [undefined, undefined];
      const min = currentMin ?? rangeFromArray?.min ?? prevMin;
      const max = currentMax ?? rangeFromArray?.max ?? prevMax;
      const rangeLabel =
        min !== undefined && max !== undefined
          ? `${min}-${max}`
          : min !== undefined || max !== undefined
            ? 'no_range'
            : 'no_range';
      return {
        department: dept,
        [rangeLabel]: weight,
        type: prev?.type ?? '',
      };
    });
  };

  const normalizeFilterIdentity = (item: any) =>
    String(item?.filterKey || item?.filterName || '')
      .toLowerCase()
      .replace(/[\s._-]+/g, '');

  const stripNumericKeys = (obj: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(obj || {}).filter(([key]) => !/^\d+$/.test(key)),
    );

  const toScalarIncludeList = (values: any[] = [], previousIncludeList: any[] = []) => {
    const includeWeightMap = new Map<string, number>();
    previousIncludeList.forEach((entry: any) => {
      if (!entry || typeof entry !== 'object') return;
      const key = Object.keys(entry).find((k) => k !== 'type');
      const val = key ? entry[key] : undefined;
      if (key && typeof val === 'number') {
        includeWeightMap.set(key, val);
      }
    });
    return values.map((value) => ({
      [value]: includeWeightMap.get(value) ?? 2,
      type: '',
    }));
  };

  const normalizeEditPayloadConsistency = (item: any) => {
    const trimmedFilterKey = typeof item?.filterKey === 'string' ? item.filterKey.trim() : item?.filterKey;
    const baseItem = {
      ...item,
      ...(trimmedFilterKey ? { filterKey: trimmedFilterKey } : {}),
    };
    const normalizedFilterKey = normalizeFilterIdentity(baseItem);
    const rangeToLabel = (range: any) => {
      if (!range || typeof range !== 'object') return '';
      const min = range?.min;
      const max = range?.max;
      if (min != null && max != null) return `${min}-${max}`;
      if (min != null) return `${min}+`;
      if (max != null) return `+${max}`;
      return '';
    };

    if (normalizedFilterKey === 'employeecount') {
      const rangeIncludes = Array.isArray(baseItem?.range)
        ? baseItem.range.map(rangeToLabel).filter(Boolean)
        : [];
      const includes = Array.isArray(baseItem?.includes) && baseItem.includes.length > 0
        ? baseItem.includes
        : rangeIncludes;

      return {
        ...baseItem,
        filterKey: 'employeeCount',
        filterName: baseItem?.filterName || 'Employee Count',
        includes,
        includeList: toScalarIncludeList(includes, baseItem?.includeList || []),
      };
    }

    if (
      normalizedFilterKey === 'employeebydepartment' ||
      normalizedFilterKey === 'employeebydept'
    ) {
      const parsed = parseMaybeJson(baseItem?.includes?.[0]);
      const deptData =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      const expandedDeptData = normalizeEmployeeByDeptData(deptData);
      return {
        ...baseItem,
        includes: [JSON.stringify(expandedDeptData)],
        includeList: toDeptIncludeList(expandedDeptData, baseItem?.includeList || []),
      };
    }

    if (
      normalizedFilterKey === 'location' ||
      normalizedFilterKey === 'accounthqlocation' ||
      normalizedFilterKey === 'contactlocation'
    ) {
      const includes = Array.isArray(baseItem?.includes) ? baseItem.includes : [];
      const excludes = Array.isArray(baseItem?.excludes) ? baseItem.excludes : [];
      const includeList = toScalarIncludeList(includes, baseItem?.includeList || []);
      const excludeList = toScalarIncludeList(excludes, baseItem?.excludeList || []);

      return stripNumericKeys({
        ...baseItem,
        // Keep persistence contract canonical for location filters.
        filterKey: 'location',
        filterName: baseItem?.filterName || 'Location',
        includes,
        excludes,
        includeList,
        excludeList,
      });
    }

    return baseItem;
  };

  return extractedFilters
    .map((item) => {
      const originalFilterName = rawFilters.find((filter) => filter.filterKey === item.filterName);
      const storeIcp = filtersFromStore.find(
        (filter) => filter?.icpViewFilterPrimaryKey === item?.icpViewFilterPrimaryKey,
      );
      if (storeIcp && !checkIsCustomField(item.filterName)) {
        const newKeys = Object.keys(storeIcp).reduce((acc: any, key) => {
          if (!(key in item)) {
            acc[key] = storeIcp[key];
          }
          return {
            ...acc,
            filterName: originalFilterName?.FilterName || item.filterName,
          };
        }, {});
        return {
          ...item,
          ...newKeys,
        };
      }
      if (checkIsCustomField(item.filterName)) {
        const findCustomField = filtersFromStore.find(
          (filter) => filter.filterKey === item.filterName,
        );
        const customFilter = rawFilters.find((filter) => filter.filterKey === item.filterName);
        const customFilterName = customFilter?.FilterName || item.filterName;
        const updatedCustomFilter = {
          ...item,
          ...findCustomField,
          filterName: customFilterName,
          subCategory: customFilter?.subCategory || null,
        };
        return updatedCustomFilter;
      }
      return { ...item };
    })
    .map((item) => normalizeEditPayloadConsistency(item))
    .filter(
      (item) =>
        item.includes?.length > 0 ||
        item.excludes?.length > 0 ||
        item.range?.length > 0 ||
        item.min ||
        item.max ||
        item.fromDate ||
        item.toDate,
    );
};
const formatDateRange = (range: any) => {
  if (typeof range !== 'string') return '';
  const dateMatches = range.match(/\d{4}-\d{2}-\d{2}/g);
  return dateMatches
    ? dateMatches.map((date: string) => date.split('-').join('-')).join(' - ')
    : range;
};
function isDateOrRange(value: any) {
  if (typeof value !== 'string') return false;
  const dateTime = `(\\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])\\s(0\\d|1\\d|2[0-3]):([0-5]\\d):([0-5]\\d)`;
  const regex = new RegExp(`^${dateTime}(\\s+to\\s+${dateTime})?$`);
  return regex.test(value);
}
const handleMinMaxRange = (value: any) => {
  if (value == null) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const min = value?.min;
    const max = value?.max;
    if (min != null && max != null) return `${min}-${max}`;
    if (min != null) return `${min}+`;
    if (max != null) return `<${max}`;
    return '';
  }
  if (typeof value !== 'string') return String(value);
  if (value.includes('min.') || value.includes('max.')) {
    if (value.includes('min.')) {
      return value.replace('min.', '') + '+';
    }
    return value.replace('max.', '<');
  }
  return value;
};

const formatStructuredRange = (range: any) => {
  if (!range || typeof range !== 'object' || Array.isArray(range)) return '';
  const countries = Array.isArray(range?.countries) ? range.countries.filter(Boolean) : [];
  const states = Array.isArray(range?.states) ? range.states.filter(Boolean) : [];
  const parts: string[] = [];
  if (countries.length) parts.push(`Countries (${countries.length}): ${countries.join(', ')}`);
  if (states.length) parts.push(`States (${states.length}): ${states.join(', ')}`);
  return parts.join(' | ');
};
export const setRangeUtil = (detail: any) => {
  const structuredRange = formatStructuredRange(detail?.range);
  if (structuredRange) return structuredRange;
  return detail.name === 'Latest Funding Date' ||
    isDateOrRange(Array.isArray(detail?.range) ? detail?.range[0] : detail?.range)
    ? Array.isArray(detail?.range)
      ? formatDateRange(detail?.range[0])
      : formatDateRange(detail?.range)
    : Array.isArray(detail?.range)
      ? (detail?.range).map((item: string) => handleMinMaxRange(item)).join(', ')
      : handleMinMaxRange(detail?.range);
};
export const setExcludeRangeUtil = (detail: any) => {
  const structuredRange = formatStructuredRange(detail?.excludeRange);
  if (structuredRange) return structuredRange;
  return detail.name === 'Latest Funding Date' ||
    isDateOrRange(
      Array.isArray(detail?.excludeRange) ? detail?.excludeRange[0] : detail?.excludeRange,
    )
    ? Array.isArray(detail?.excludeRange)
      ? formatDateRange(detail?.excludeRange[0])
      : formatDateRange(detail?.excludeRange)
    : Array.isArray(detail?.excludeRange)
      ? (detail?.excludeRange).map((item: string) => handleMinMaxRange(item)).join(', ')
      : handleMinMaxRange(detail?.excludeRange);
};

const parseCurrencyValue = (val: string) => {
  if (val === 'Unavailable' || val === null) return val;

  // Remove $ and any other non-alphanumeric characters except M, B, K
  let cleanVal = val.trim();

  // Check if it's already a number
  if (!isNaN(Number(cleanVal))) {
    return Number(cleanVal);
  }

  // Handle currency formatting
  let multiplier = 1;
  if (cleanVal.endsWith('M')) {
    multiplier = 1000000;
    cleanVal = cleanVal.slice(0, -1);
  } else if (cleanVal.endsWith('B')) {
    multiplier = 1000000000;
    cleanVal = cleanVal.slice(0, -1);
  } else if (cleanVal.endsWith('K')) {
    multiplier = 1000;
    cleanVal = cleanVal.slice(0, -1);
  }

  // Remove $ and any other non-numeric characters
  cleanVal = cleanVal.replace(/[$,]/g, '');

  // Convert to number and apply multiplier
  return !isNaN(Number(cleanVal)) ? Number(cleanVal) * multiplier : null;
};

export const parseMinMaxRange = (value: string) => {
  if (value === 'Unavailable') {
    return { min: 'Unavailable', max: 'Unavailable' };
  }

  if (value.startsWith('max.')) {
    const max = value.replace('max. ', '');
    return { min: null, max: parseCurrencyValue(max) };
  }

  if (value.startsWith('min.')) {
    const min = value.replace('min. ', '');
    return { min: parseCurrencyValue(min), max: null };
  }

  // Handle range format like "$10M - $100M"
  const parts = value.split('-').map((part) => part.trim());
  if (parts.length === 2) {
    return {
      min: parseCurrencyValue(parts[0]),
      max: parseCurrencyValue(parts[1]),
    };
  }

  // Fallback to original logic for other formats
  const [min, max] = value
    .split('-')
    .map((item) =>
      item === 'Unavailable' ? item : item === null ? item : parseCurrencyValue(item),
    );

  return { min, max };
};

export const checkIsCustomField = (filterName: string) => {
  return (
    filterName.startsWith('string') ||
    filterName.startsWith('integer') ||
    filterName.startsWith('boolean') ||
    filterName.startsWith('singleList') ||
    filterName.startsWith('multiList') ||
    filterName.startsWith('date')
  );
};
